export const MIN_REMOTE_QUERY_LENGTH = 2;
export const MAX_RESULTS = 12;
// Short enough that results land within a keystroke of the user stopping, long
// enough that a fast typist does not fire a request per character.
export const REMOTE_SEARCH_DEBOUNCE_MS = 90;
export const RECORD_CACHE_LIMIT = 50;
// The partner list ships with the bar and matches locally, so its cap is about how much
// of it is worth scrolling rather than about request cost.
export const MAX_PARTNER_RESULTS = 50;
// The endpoint only matches claim IDs as substrings from three characters up (below
// that it matches them exactly), so a two-character miss says nothing about longer
// queries and cannot be extended locally.
export const EMPTY_PREFIX_MIN_LENGTH = 3;
// Pages, saved views, actions, and configuration tabs ship with the navbar and have no
// server pass, so they must match locally.
const LOCAL_TYPES = ["page", "view", "action", "setting"];
// Configuration tabs are one level below the Configuration page, so they are listed only
// once something is typed rather than crowding the blank-query list.
const BLANK_QUERY_TYPES = ["page", "view", "action"];

export function isCommandSearchShortcut(event) {
  return event.key?.toLowerCase() === "k" &&
    (event.metaKey || event.ctrlKey) &&
    !event.altKey;
}

export function platformModifierLabel(platform = "") {
  return /Mac|iPhone|iPad|iPod/i.test(platform) ? "⌘" : "Ctrl";
}

export function normalizeTransientNavigationState({ overlay, body, previousBodyOverflow }) {
  if (overlay) overlay.hidden = true;

  if (body && previousBodyOverflow !== undefined) body.style.overflow = previousBodyOverflow;
}

// recordsAreCurrent says whether the records were matched by the endpoint against this
// exact query. Records left over from a previous query must match locally to stay on
// screen, so a keystroke cannot leave an unrelated record selectable while the next
// request is in flight.
export function rankNavigationResults(shortcuts, records, query, { recordsAreCurrent = true } = {}) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const candidates = normalizedQuery ? [...shortcuts, ...records] : [...shortcuts];

  const scored = candidates
    .map((result) => ({ result, score: resultScore(result, normalizedQuery, recordsAreCurrent) }))
    .filter(({ score }) => score !== null)
    .sort((left, right) => left.score - right.score || typeOrder(left.result.type) - typeOrder(right.result.type));

  // An action is the only way into the view it opens, so the cap must not be able to drop
  // one: actions sort after pages and saved views, and a partner with enough saved views
  // would otherwise hide Switch Partners behind a query the user has to already know.
  // Actions spend the budget rather than adding to it, so the result count still holds.
  const budget = Math.max(MAX_RESULTS - scored.filter(({ result }) => result.type === "action").length, 0);
  let kept = 0;
  const ranked = scored
    .filter(({ result }) => result.type === "action" || kept++ < budget)
    .map(({ result }) => result);

  // The dialog renders a heading whenever the type changes, so keep each type contiguous
  // (in order of its best-scoring member) rather than emitting "Claims" twice.
  return groupByType(ranked);
}

// The partner list is a secondary search view: one flat, already alphabetical group that
// the query only filters. Ranking it would reorder it, and an admin looking for a partner
// by name is served better by the alphabetical position they expect. The query matches the
// slug (the row's meta) as well as the name, since the slug is what an admin sees in URLs
// and logs, and it is the only thing separating two partners with similar names.
export function rankPartnerResults(partners, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const matches = normalizedQuery
    ? partners.filter((partner) => [partner.title, partner.meta].some((field) => String(field || "").toLowerCase().includes(normalizedQuery)))
    : partners;

  return matches.slice(0, MAX_PARTNER_RESULTS);
}

function groupByType(results) {
  const groups = new Map();

  results.forEach((result) => {
    const group = groups.get(result.type);
    group ? group.push(result) : groups.set(result.type, [result]);
  });

  return [...groups.values()].flat();
}

// Keystroke-level cache of the record payloads the endpoint returned. Backspacing,
// retyping, and typing past a query that matched nothing all resolve locally, so the
// only keystrokes that wait on the network are ones that can produce new records.
export class NavigationRecordCache {
  constructor(limit = RECORD_CACHE_LIMIT) {
    this.limit = limit;
    this.entries = new Map();
  }

  get(query) {
    return this.entries.get(normalizeCacheKey(query));
  }

  set(query, records) {
    const key = normalizeCacheKey(query);
    this.entries.delete(key);
    this.entries.set(key, records);
    if (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value);
  }

  // Substring matching is monotonic — every record matching a longer query also
  // matches its prefixes — so a prefix that matched nothing guarantees this query
  // matches nothing either.
  hasEmptyPrefix(query) {
    const key = normalizeCacheKey(query);

    return [...this.entries].some(([cachedKey, records]) => (
      records.length === 0 && cachedKey.length >= EMPTY_PREFIX_MIN_LENGTH && key.startsWith(cachedKey)
    ));
  }
}

function normalizeCacheKey(query) {
  return String(query || "").trim().toLowerCase();
}

function resultScore(result, query, recordsAreCurrent) {
  const isLocalShortcut = LOCAL_TYPES.includes(result.type);
  if (!query) return BLANK_QUERY_TYPES.includes(result.type) ? 100 : null;

  const title = String(result.title || "").toLowerCase();
  const keywords = String(result.keywords || "").toLowerCase();
  const meta = String(result.meta || "").toLowerCase();

  if (title === query) return 0;
  if (title.startsWith(query)) return 10;
  if (title.includes(query)) return 20;
  if (keywords.split(/\s+/).some((keyword) => keyword.startsWith(query))) return 30;
  if (keywords.includes(query)) return 40;
  if (meta.includes(query)) return 50;

  // The endpoint already decided this record matches, and it matches on things the
  // payload does not carry: member name words in any order, the employer a member
  // belongs to, a billing provider name shadowed by a differently named resolved
  // facility. Dropping those would hide results the server went to the trouble of
  // finding, so records rank last rather than disappear. Local shortcuts have no such
  // server pass and must match here, and neither do records the server matched against
  // some earlier query.
  return isLocalShortcut || !recordsAreCurrent ? null : 60;
}

function typeOrder(type) {
  return { page: 0, view: 1, setting: 2, action: 3, claim: 4, member: 5 }[type] ?? 6;
}
